import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '../context/ModalContext';
import { LoginPanel } from './LoginPanel';
import { ForgotPanel } from './ForgotPanel';
import { ContactPanel } from './ContactPanel';

/**
 * Single React-controlled modal host. Replaces Bootstrap's modal JS for the
 * marketing modals: exactly one modal and one backdrop exist at a time and
 * are driven purely by React state, so chaining (e.g. login → forgot →
 * login) can never leave an orphan backdrop. BrightSaaS (.modal /
 * .modal-dialog / .modal-content / .modal-backdrop) classes are reused for
 * styling; we just supply the `show` state Bootstrap would normally toggle.
 */
export function BrightSaaSModalHost() {
  const { active, close } = useModal();

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    document.documentElement.classList.add('scroll-off');
    const prevBodyOverflow = document.body.style.overflow;
    const prevRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('scroll-off');
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevRootOverflow;
    };
  }, [active, close]);

  if (!active) return null;

  return createPortal(
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) close();
        }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <button
              type="button"
              aria-label="Close"
              onClick={close}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                zIndex: 2,
                width: '2.25rem',
                height: '2.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontSize: '1.25rem',
                lineHeight: 1,
                cursor: 'pointer',
              }}>
              <em className="icon ni ni-cross" />
            </button>
            <div className="modal-body">
              {active === 'login' && <LoginPanel />}
              {active === 'forgot' && <ForgotPanel />}
              {active === 'contact' && <ContactPanel />}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
